/**
 * useCameraDevices — Hook to enumerate camera devices, classify physical vs virtual cameras,
 * and manage active selected camera.
 */

import { useCallback, useEffect, useState } from "react";

export interface CameraDevice {
  deviceId: string;
  label: string;
  isVirtual: boolean;
}

const VIRTUAL_KEYWORDS = [
  "splitcam",
  "obs virtual",
  "spout",
  "unity",
  "camo",
  "manycam",
];

export function useCameraDevices() {
  const [devices, setDevices] = useState<CameraDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");

  const refreshDevices = useCallback(async () => {
    try {
      if (typeof navigator === "undefined" || !navigator.mediaDevices?.enumerateDevices) {
        return;
      }

      // Prompt for permission to ensure device labels are accessible
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach((track) => track.stop());
      } catch (e) {
        console.warn("[useCameraDevices] Camera permission prompt failed or denied:", e);
      }

      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = allDevices.filter((d) => d.kind === "videoinput");

      const mappedDevices: CameraDevice[] = videoDevices.map((d, index) => {
        const label = d.label || `Camera ${index + 1}`;
        const labelLower = label.toLowerCase();
        const isVirtual = VIRTUAL_KEYWORDS.some((kw) => labelLower.includes(kw));
        return {
          deviceId: d.deviceId,
          label,
          isVirtual,
        };
      });

      setDevices(mappedDevices);

      if (mappedDevices.length > 0) {
        // Find first physical (non-virtual) camera
        const firstPhysical = mappedDevices.find((d) => !d.isVirtual);
        setSelectedDeviceId((prev) => {
          // Keep current selection if still valid
          if (prev && mappedDevices.some((d) => d.deviceId === prev)) {
            return prev;
          }
          // Default to first physical camera, or fallback to first overall device
          return firstPhysical ? firstPhysical.deviceId : mappedDevices[0].deviceId;
        });
      }
    } catch (err) {
      console.error("[useCameraDevices] Failed to enumerate camera devices:", err);
    }
  }, []);

  useEffect(() => {
    refreshDevices();

    if (typeof navigator !== "undefined" && navigator.mediaDevices?.addEventListener) {
      const handleDeviceChange = () => refreshDevices();
      navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);
      return () => {
        navigator.mediaDevices.removeEventListener("devicechange", handleDeviceChange);
      };
    }
  }, [refreshDevices]);

  return {
    devices,
    selectedDeviceId,
    setSelectedDeviceId,
    refreshDevices,
  };
}
